<template>
  <div ref="swiper" class="swiper-container">
    <Loader />
    <Gate />

    <div :class="{ 'fixed-logo--moved': currentSlide > 0 }" class="fixed-logo">
      <LogoLink />
    </div>

    <!-- mobile global items -->
    <MobileFixedTop
      :current-page="currentSlide"
      class="mobile"
      @click="changeSlide"
    />

    <div ref="bottle" class="bottle-wrapper bottle-wrapper--mobile-intro">
      <img
        src="/images/bottle.png"
        alt="Królewskie butelka"
        class="img img--bottle bottle"
      />
    </div>
    <!-- e/o mobile global items -->

    <SectionsNavigation
      :current-page="currentSlide"
      class="section-nav"
      :class="{
        'section-nav--visible': currentSlide && !$store.state.isGateOpen
      }"
      @click="changeSlide"
    />
    <!-- e/o desktop global items -->

    <main class="swiper-wrapper">
      <!-- SLIDES -->

      <!-- desktop intro -->
      <IntroDesktop
        class="swiper-slide swiper-slide--desktop"
        :class="{ 'swiper-slide--active': currentSlide === 0 }"
        @click="changeSlide"
      />
      <!-- e/o desktop intro -->

      <!-- mobile intro -->
      <IntroMobile
        ref="intro-mobile"
        class="swiper-slide swiper-slide--mobile"
      />

      <LinksRules
        :class="{ 'swiper-slide--active-mobile': currentSlide === 1 }"
        class="swiper-slide swiper-slide--mobile"
      />
      <!-- e/o mobile intro -->

      <SectionComponent
        v-for="(section, idx) in sections"
        :key="section.name"
        :is-last-section="idx + 1 === sections.length"
        :is-logo-visible="!currentSlide"
        :section="section"
        class="swiper-slide"
        :class="{
          'swiper-slide--active': section.isActiveSection,
          'swiper-slide--active-mobile': section.isActiveSectionMobile
        }"
        @loadedImgMobile="onImgMobileLoad"
      />
      <!-- E/O SLIDES -->
    </main>

    <!-- popups -->
    <Ingredients />

    <Contact />
    <!-- e/o popups -->
  </div>
</template>

<script>
import IntroDesktop from '@/components/Intro/Desktop'
import IntroMobile from '@/components/Intro/Mobile'
import MobileFixedTop from '@/components/modules/MobileFixedTop'
import LinksRules from '@/components/modules/LinksRules'
import SectionsNavigation from '@/components/sections/SectionsNavigation'
import SectionComponent from '@/components/sections/Section'
import Ingredients from '@/components/popups/Ingredients'
import Contact from '@/components/popups/Contact'
import Gate from '@/components/Gate'
import Loader from '@/components/Loader'

import 'swiper/dist/css/swiper.min.css'
import Swiper from 'swiper'
import gsap from 'gsap'
import addMetaDescription from '@/assets/js/addMetaDescription'

export default {
  components: {
    IntroDesktop,
    IntroMobile,
    MobileFixedTop,
    LinksRules,
    SectionComponent,
    SectionsNavigation,
    Ingredients,
    Contact,
    Gate,
    Loader
  },
  data: () => ({
    direction: '',
    lastClientX: 0,
    swiper: {},
    allowDataUrlUpdate: true,
    nrOfLoadedImgMobile: 0
  }),
  computed: {
    sections() {
      return this.$store.getters.sections
    },
    initialSlide() {
      return this.$store.state.initSlideIdxFromUrlOnDesktop
    },
    currentSlide() {
      return this.$store.state.currentSlide
    },
    swiperEffectName() {
      const ua = window.navigator.userAgent
      const isEdge = parseInt(
        ua.substring(
          ua.indexOf('Edge/') + 5,
          ua.indexOf('.', ua.indexOf('Edge/'))
        ),
        10
      )
      const isSafari = /^((?!chrome|android).)*safari/i.test(ua)

      return isEdge || isSafari ? 'coverflow' : 'slide'
    }
  },
  watch: {
    // fix current page on horizontal/vertical view changed
    direction(newDirection) {
      // first slide on mobile and desktop has the same slide idx
      const isFirstSlide = this.currentSlide === 0
      // prevent slide change on initial page rendering
      const isInitialRendering = !this.$store.getters.isPageLoaded
      // last slide from mobile to desktop automatically change on the last slide
      const isChangeFromLastSlideOnMobile =
        newDirection === 'horizontal' &&
        this.currentSlide === this.sections.length

      if (isFirstSlide || isInitialRendering || isChangeFromLastSlideOnMobile)
        return

      newDirection === 'vertical'
        ? this.swiper.slideTo(this.currentSlide + 1) // section slide-idx on mobile (two intro slides)
        : this.swiper.slideTo(this.currentSlide - 1) // section slide-idx on desktop (one intro slide)
    },
    '$store.getters.isAnyPopupOpen'(value) {
      this.swiper.allowTouchMove = !value

      value
        ? this.disableSwiper() // when open popup
        : this.enableSwiper() // when close popup

      this.checkScrollingPossibilityOnCookiesToggle()
    },
    '$store.state.isGateOpen'(value) {
      if (!value) {
        // fix 100vh positioning in sections on mobile (bug after add lazy-loading)
        window.dispatchEvent(new Event('resize'))
        this.checkScrollingPossibilityOnCookiesToggle()
      }
    },
    currentSlide(value) {
      if (value === 0 && this.swiper.activeIndex !== 0) {
        // when click on logo link
        const idxSectionFrom = this.swiper.activeIndex
        this.$nextTick(() => this.bottleBackwardAnimation(idxSectionFrom))
        this.swiper.slideTo(this.currentSlide)
      }
    }
  },
  mounted() {
    // eslint-disable-next-line
    this.swiper = new Swiper(this.$refs.swiper, {
      direction: 'horizontal',
      initialSlide: this.initialSlide,
      mousewheel: true,
      allowTouchMove: false,
      speed: 1200,
      simulateTouch: false,
      centeredSlides: true,
      preloadImages: true,
      lazy: true,
      effect: this.swiperEffectName,
      coverflowEffect: {
        rotate: 0,
        slideShadows: false,
        depth: 2
      },
      keyboard: {
        enabled: true
      },
      breakpoints: {
        1170: {
          direction: 'vertical',
          initialSlide: this.initialSlide && this.initialSlide + 1,
          followFinger: false,
          allowTouchMove: true,
          speed: 600,
          lazy: false
        }
      },
      on: {
        init: () => {
          // fix 100vh positioning in sections on mobile (bug after add lazy-loading)
          const dispatchResizeEvent = () => {
            setTimeout(() => {
              if (this.$store.getters.isPageLoaded) {
                window.dispatchEvent(new Event('resize'))
              } else {
                dispatchResizeEvent()
              }
            }, 400)
          }

          dispatchResizeEvent()
        },
        slideChange: () => {
          const isSwipingBackward = this.swiper.activeIndex < this.currentSlide
          this.$store.commit('setCurrentSlide', this.swiper.activeIndex)

          if (this.allowDataUrlUpdate) this.updateUrlData()
          this.allowDataUrlUpdate = true

          if (
            isSwipingBackward &&
            this.direction === 'vertical' &&
            !this.currentSlide
          ) {
            this.$nextTick(() => this.bottleBackwardAnimation())
            return
          }

          if (this.currentSlide) {
            this.$nextTick(() => this.enterAnimation())
          }
        },
        resize: () => {
          this.direction = this.swiper.params.direction
          this.checkScrollingPossibilityOnCookiesToggle()
        }
      }
    })

    this.direction = this.swiper.params.direction
    if (this.$store.state.isGateOpen) this.disableSwiper()
    this.checkScrollingPossibilityOnCookiesToggle()
    this.initialSlide &&
      this.$store.commit('setCurrentSlide', this.swiper.activeIndex)

    if (this.direction === 'horizontal') {
      // desktop
      // load images from intro video
      this.swiper.lazy.loadInSlide(0)
    }

    window.addEventListener('popstate', this.onBrowserArrowDetect)
    this.$once('hook:beforeDestroy', () => {
      window.removeEventListener('popstate', this.onBrowserArrowDetect)
    })
  },
  methods: {
    changeSlide(idx) {
      if (this.$store.state.isMobileMenuOpen) {
        this.$store.commit('toggleMobileMenu')
      }

      this.direction === 'horizontal'
        ? this.swiper.slideTo(idx + 1)
        : this.swiper.slideTo(idx + 2)
    },
    checkScrollingPossibilityOnCookiesToggle() {
      // enable scrolling on desktop on only cookies popup open
      if (!this.$store.state.isCookiesOpen || this.$store.state.isGateOpen)
        return

      this.direction === 'vertical' ? this.disableSwiper() : this.enableSwiper()
    },
    disableSwiper() {
      this.swiper.keyboard.disable()
      this.swiper.mousewheel.disable()
      this.swiper.detachEvents()
    },
    enableSwiper() {
      this.swiper.keyboard.enable()
      this.swiper.mousewheel.enable()
      this.swiper.attachEvents()
    },
    enterAnimation() {
      if (this.direction === 'horizontal') {
        // desktop
        gsap.from('.swiper-slide--active .section-enter-animation', {
          x: '100vw',
          duration: 1.5,
          ease: 'power1.easeOut'
        })
      } else {
        // mobile
        const items = document.querySelector(
          '.swiper-slide--active-mobile .section-enter-animation-mobile'
        ).childNodes
        const bottle = this.$refs.bottle
        const animateBottle = this.currentSlide === 1
        const tl = gsap.timeline()

        tl.addLabel('start')
          .to(bottle, {
            y: '-150vh',
            duration: animateBottle ? 0.7 : 0,
            delay: 0.12,
            ease: 'power2.easeInOut'
          })
          .from(
            items,
            {
              y: 200,
              delay: 0.2,
              duration: 0.5,
              ease: 'power2.easeOut',
              stagger: { amount: 0.2 }
            },
            'start'
          )
      }
    },
    bottleBackwardAnimation(idxSectionFrom = 1) {
      const { bottle } = this.$refs

      if (idxSectionFrom === 1) {
        gsap.to(bottle, { y: '-40vh', duration: 0.6, ease: 'power3.easeInOut' })
      } else {
        const tl = gsap.timeline()

        tl.set(bottle, { opacity: 0 })
          .to(bottle, { y: '30vh', duration: 0.1 })
          .set(bottle, { opacity: 1 })
          .to(bottle, {
            y: '-40vh',
            duration: 0.6,
            delay: 0.4,
            ease: 'power2.easeInOut'
          })
      }
    },
    updateUrlData() {
      if (this.$store.state.isGateOpen) return

      const activeSection =
        this.direction === 'horizontal'
          ? this.sections.find(section => section.isActiveSection)
          : this.sections.find(section => section.isActiveSectionMobile)

      if (activeSection) {
        window.history.pushState({}, null, `/${activeSection.slug}/`)
        document.title = `Królewskie — ${activeSection.navTitle}`
        addMetaDescription(activeSection.metaDescription)
      } else {
        window.history.pushState({}, null, '/')
        document.title = 'Królewskie'
        addMetaDescription(this.$store.state.metaDescription)
      }
    },
    onBrowserArrowDetect() {
      this.allowDataUrlUpdate = false
      const { name, params } = this.$route

      if (name === 'home') {
        this.swiper.slideTo(0)
      } else if (name === 'section') {
        const currentSectionIdx = this.$store.getters.getSectionIdxBySlug(
          params.section
        )
        this.changeSlide(currentSectionIdx)
      }
    },
    onImgMobileLoad() {
      if (this.$store.getters.isPageLoaded) return
      this.nrOfLoadedImgMobile++
      this.$store.commit(
        'setLoadedProgress',
        (this.nrOfLoadedImgMobile / this.$store.state.sections.length) * 100
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.swiper-container {
  width: 100%;
  height: $vh;
  max-height: 100%;
}

.swiper-wrapper {
  height: $vh;
}

.swiper-slide {
  overflow-x: hidden;
  max-height: 100%;
  height: 100% !important;

  &--mobile {
    @include media-up(lg) {
      display: none !important;
    }
  }

  &--desktop {
    @include hide-down(lg);
  }
}

.mobile {
  @include hide-up(lg);
}

.section-nav {
  pointer-events: none;
  transition: opacity 0.3s;
  opacity: 0;

  &--visible {
    opacity: 1;
  }
}

.logo {
  @include hide-down(lg);
  position: fixed;
  top: 50px;
  left: 5vw;
  z-index: z(logo);

  opacity: 1;
  transition: opacity 0s 1.2s;
  // delay from gsap animation time

  &--hidden {
    opacity: 0;
    pointer-events: none;
    transition-delay: unset;
  }
}

.bottle-wrapper {
  @include hide-up(lg);
  @include center-content(horizontally);

  position: absolute;
  bottom: -50vh;
  height: 70vh;
  width: 100%;
  z-index: z(mobile-intro-bottle);
}

.bottle {
  height: 100%;
}

.fixed-logo {
  @include hide-down(lg);
  @include center-self(horizontally, $position: fixed);

  top: 24px;

  z-index: z(slider-wrapper);
  transition: transform 1.2s ease;

  &--moved {
    transform: translateX(calc(-50vw + 50%));
  }
}
</style>
